import { createMemo, type VoidComponent } from "solid-js";
import type { Group } from "../../state/group.ts";
import { groupAvatar, memberAvatar } from "../../utils/avatarUtil.ts";
import type { User } from "../../state/user.tsx";
import type { Invite } from "../../state/invite.ts";

type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";
export type AvatarProps = {
  value: string;
  size: AvatarSize;
  shape: "circle" | "square";
  backgroundColor: "brand" | "blue" | "purple" | "pink" | "gray" | "orange" | "green";
};

const resolveBackgroundColor = (color: AvatarProps["backgroundColor"]): string => {
  switch (color) {
    case "brand":
      return "bg-accent-brand";
    case "blue":
      return "bg-accent-blue";
    case "purple":
      return "bg-accent-purple";
    case "pink":
      return "bg-accent-pink";
    case "gray":
      return "bg-accent-gray";
    case "orange":
      return "bg-accent-orange";
    case "green":
      return "bg-accent-green";
  }
};

export const Avatar: VoidComponent<AvatarProps> = (props) => (
  <div
    class="flex shrink-0 items-center justify-center bg-accent-brand text-inverse uppercase"
    classList={{
      "rounded-full": props.shape === "circle",
      rounded: props.shape === "square",
      "size-6 text-extra-small-semibold": props.size === "xs",
      "size-8 text-extra-small-semibold": props.size === "sm",
      "size-9 text-base-semibold": props.size === "md",
      "size-10 text-base-semibold": props.size === "lg",
      "size-11 text-base-semibold": props.size === "xl",
      [resolveBackgroundColor(props.backgroundColor)]: true,
    }}
  >
    {props.value}
  </div>
);

export const GroupAvatar: VoidComponent<{ group: Group; size: AvatarSize }> = (props) => {
  const avatar = createMemo(() => groupAvatar(props.group));
  return (
    <Avatar
      size={props.size}
      value={avatar().initials}
      shape={avatar().shape}
      backgroundColor={avatar().backgroundColor}
    />
  );
};

export const MemberAvatar: VoidComponent<{ member: User | Invite; size: AvatarSize }> = (props) => {
  const avatar = createMemo(() => memberAvatar(props.member));
  return (
    <Avatar
      size={props.size}
      value={avatar().initials}
      shape={avatar().shape}
      backgroundColor={avatar().backgroundColor}
    />
  );
};
